import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { assignViewsState, callOnLoadOnColumns, getIsArchivedForBoard } from "../../../other/utils/otherUtils";
import { ALERT_TYPES, SWIVU_ASSIST_CONFIG } from "../../../other/constants/OtherKeys.jsx";
import {
  cellMenuItemRemoveRow,
  processCellMenuItems
} from "../../../other/utils/gridCellMenuUtils.jsx";
import {
  swivuCheckGridGrants,
  lineThroughRenderer,
  defaultGroupRenderer as groupRenderer,
  getOriginalRecord,
  handleGridDeleteButtonState,
  handleSocketChangesOnGridStore
} from "../../../other/utils/utils.jsx";
import { getSavedBoardSelection } from "../../../react/hooks/useBoards.jsx";
import { DateHelper, Store } from "@bryntum/schedulerpro";
import { SwivuStoreArchiveMixin } from "../../stores/mixins/SwivuStoreArchiveMixin.jsx";
import { updateTabTitleWithRecordCount } from "../../../other/utils/editorUtils.jsx";
import { RECURRING_INTERVALS } from "../../models/Reminder.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";

const tabTitle = "Reminders";

export const HasReminderBaseMixin = (target) =>
  class extends target {
    static get $name() {
      return "ReminderTabBase";
    }

    static get configurable() {
      return {
        title: tabTitle,
        tab : {
          icon: "fa-regular fa-bell w-4 h-4"
        },
        defaults: {
          labelWidth: 104
        },
        items: {
          remindersGrid: {
            preserveScrollOnDatasetChange: true,
            cls: "swivu-empty-text-widget",
            type: WIDGET_KEYS.swivuModernGrid,
            swivuGridTitle: "Reminders",
            height: "100%",
            features: {
              rowReorder: { showGrip: true, disabled: true },
              cellMenu: {
                items: {
                  cut: false,
                  copy: false,
                  paste: false,
                  removeRow: cellMenuItemRemoveRow({
                    onItem: "up.onDeleteReminder",
                    icon: 'fa-regular fa-trash !text-danger'
                  }),
                }
              }
            },
            listeners: {
              cellClick: "up.onCellClick",
              beforeCellEditStart: "up.onBeforeCellEdit",
            },
            columns: [
              {
                id: "description",
                field: "description",
                text: "Description",
                type: WIDGET_KEYS.swivuSortColumn,
                align: "left",
                flex: 1,
                minWidth: 100,
                renderer: ({ value, record }) => lineThroughRenderer(value, record.archived),
                groupRenderer,
                finalizeCellEdit: "up.onFinalizeDescriptionField",
              },
              {
                id: "alertDate",
                type: "date",
                format: "ddd MM/DD/YY",
                field: "_alertDate",
                text: "Date",
                align: "left",
                flex: 1,
                minWidth: 100,
                editor: {
                  keepTime: true,
                  required: true,
                },
                finalizeCellEdit: "up.onFinalizeAlertDate"
              },
              {
                id: "alertTime",
                type: "time",
                field: "_alertTime",
                text: "Time",
                align: "left",
                flex: 1,
                minWidth: 100,
                editor: {
                  keepDate: true,
                  required: true,
                },
                finalizeCellEdit: "up.onFinalizeAlertTime"
              },
              {
                id: "recurringInterval",
                // Field added for grouping
                // Bug: When grouping is applied on this column, changing a field's value in any group crashes the widget
                text: "Recurring",
                minWidth: 100,
                type: WIDGET_KEYS.recurringIntervalColumn,
                groupable: false,
              },
              {
                id: "repeatCount",
                field: "repeatCount",
                text: "Recurring Count",
                align: "center",
                flex: 1,
                minWidth: 100,
                min: 0,
                type: "number",
                finalizeCellEdit: "up.onFinalizeRepeatCount",
              },
            ],
            tbar: {
              items: {
                remindersGridAddButton: {
                  type: "button",
                  cls: 'swivu-modern-button b-blue b-raised',
                  text: "Add Reminder",
                  icon: "b-fa b-fa-plus b-button-icon",
                  onClick: "up.onAddReminderClick"
                },
              }
            },
          }
        },
        swivuAssistance: SWIVU_ASSIST_CONFIG.reminder
      };
    }

    onFinalizeDescriptionField({ value, editorContext: { record } }) {
      if (!value?.length) {
        return "Please enter reminder description";
      } else if (record.isPhantom) {
        this.project.reminderStore.add(record);
      }
      return true;
    }

    // Todo: rowReorder is broken find a reliable way to provide `project` instance to the place from where error is thrown
    loadRecord(record, context, otherParams) {
      if (otherParams.isFirstShow) {
        super.loadRecord(record, context);
        const { remindersGrid } = this.widgetMap;
        const remindersGridDeleteButton = remindersGrid.tbar.widgetMap.remindersGridDeleteButton;

        const selectedBoard = getSavedBoardSelection();
        this.swivuIsDisplayArchiveRecords = getIsArchivedForBoard(selectedBoard.slug);

        // to make archiving work, make sure to assign store before assigning the state to the grid
        // Bug: New Records are not reflected in the store via websocket, Editor needs to be refreshed
        remindersGrid.store = new (SwivuStoreArchiveMixin(Store))({data: this.getStoreData()});

        remindersGrid.stateId = this.stateId;
        assignViewsState([remindersGrid], this.stateProvider);
        callOnLoadOnColumns(remindersGrid.columns, context.project)
        remindersGrid.features.cellMenu.processItems = processCellMenuItems({ grid: remindersGrid, user: context.user });
        swivuCheckGridGrants(remindersGrid, context.user);
        this.swivuReminderGridUpdateListener = updateTabTitleWithRecordCount(this, remindersGrid.store, tabTitle);
        if (!this.swivuDeleteButtonListener) {
          this.swivuDeleteButtonListener = handleGridDeleteButtonState(remindersGrid, remindersGridDeleteButton);
        }
        handleSocketChangesOnGridStore(remindersGrid, this.project.reminderStore, this.getStoreData.bind(this));
      }
    }

    onAddReminderClick() {
      // since onAddReminder is referenced with up.onAddReminder in click event and the onAddReminder is being called from
      // multiple places then the first argument from button click would be click event object when click add button
      // that's why we're using onAddReminderClick in add button event
      this.onAddReminder();
    }

    onAddReminder(data = {}) {
      const { record } = this;
      const { remindersGrid } = this.widgetMap;
      const lastItemOrder = remindersGrid.store.last?.order;
      const currentItemOrder = lastItemOrder ? lastItemOrder + 1 : 0;
      const reminderRecord = this.project.reminderStore.createRecord({
        [this.recordType]: record.id,
        entityType: this.entityType,
        order: currentItemOrder,
        alertType: ALERT_TYPES.REMINDER,
        archived: record.archived,
        // 5 min forward
        alertDate: DateHelper.add(new Date(), 5, "minute").toISOString(),
        description: "",
        repeatCount: RECURRING_INTERVALS[0].defaultCount,
        recurringInterval: RECURRING_INTERVALS[0].id,
        ...data
      });
      reminderRecord.project = this.project; // temporary doing as we don't have access to project in model of Reminder if it's created with createRecord function - breaking on Notify page
      remindersGrid.store.add(reminderRecord);
      remindersGrid.startEditing({ record: reminderRecord, field: "description" });
    }

    onDeleteReminder() {
      const { remindersGrid } = this.widgetMap;
      const selectedIds = remindersGrid.selectedRecords.map((r) => r.id);
      remindersGrid.store.remove(selectedIds);
      this.project.reminderStore.remove(selectedIds);
    }

    getEditableField() {
      return false;
    }

    onCellClick({ column, record, event }) {
      if(column.id == "recurringInterval") {
         const originalRecord = getOriginalRecord(record);
         this.editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: originalRecord,
          event,
          column
        }, { by: "recordType", useExisting: true });
      }
    }

    onSwivuEditorHide() {
      super.onSwivuEditorHide();
      if (this.swivuReminderGridUpdateListener) {
        this.swivuReminderGridUpdateListener();
      }
    }

    onBeforeCellEdit({ editorContext: { record, column } }) {
      const repeatCountStatus = record.recurringInterval === 0 && column.id === "repeatCount";
      return !repeatCountStatus
    }

    onFinalizeAlertDate({ value }) {
      if (DateHelper.isBefore(value, new Date())) {
        return "Cannot set alert date in the past"
      }
      return true;
    }

    onFinalizeAlertTime({ value, record }) {
      const currentDate = new Date();
      const selectedDateTime = DateHelper.clone(record.alertDate);
      selectedDateTime.setHours(value.getHours(), value.getMinutes(), value.getSeconds(), 0);
      if (DateHelper.isBefore(selectedDateTime, currentDate)) {
        return "Cannot set alert time in the past";
      }
      return true;
    }

    onFinalizeRepeatCount({ value, record }) {
      if (value === 0) {
        return "Cannot set repeat count to zero, Set recurring to Never";
      }
      else if (value < record.releasedCount) {
        return "New repeat count should be greater than the current repeat count"
      }
      return true;
    }
  };
